/**
 * Editing a kiosk's menu pages: adding, renaming and deleting them, placing
 * buttons on them, and what is worth saying about them.
 *
 * Pure functions over the document, like the rest of editing.ts, so that undo is
 * the editor keeping the previous document and nothing here needs to know about it.
 * What the pages mean at a branch is lib/kioskPages.ts; what the server refuses is
 * internal/layout/pages.go.
 */

import { MENU_PAGE, onCall, onScreen, pageIds, textIn, type ButtonAction, type Fault, type KioskScreen, type LayoutDocument, type LayoutElement, type LayoutPage, type Screen } from './types'
import { CALL_ONLY, CALLED_KINDS } from './calledScreen'
import { COUNTER_HOME, COUNTER_SCREENS } from './counter'
import { t, type StaffLanguage } from '../lib/staffText'

/** How many pages a design may have. Mirrors layout.MaxPages. */
export const MAX_PAGES = 40

/** The menu's name in a design that never named it. */
const contentLang = (lang: string): StaffLanguage => (lang === 'ru' || lang === 'az' ? lang : 'en')

/** A design's pages written out, so that one can be added beside the menu. A
 *  design saved before pages has none and means the menu alone. */
export function withPages(doc: LayoutDocument): LayoutDocument {
  if (doc.pages && doc.pages.length > 0) return doc
  return { ...doc, pages: [{ id: MENU_PAGE, name: { az: t('designs.ed.menu', 'az'), ru: t('designs.ed.menu', 'ru'), en: t('designs.ed.menu', 'en') } }] }
}

/** What a page is called in the editor: its name, or its place. */
export function pageLabel(doc: LayoutDocument, id: string, lang = 'az'): string {
  const pages = withPages(doc).pages!
  const i = pages.findIndex((p) => p.id === id)
  const named = textIn(pages[i]?.name, lang)
  if (named) return named
  return i === 0 ? t('designs.ed.menu', contentLang(lang)) : t('designs.ed.pageN', contentLang(lang), { n: i + 1 })
}

function freshPageId(doc: LayoutDocument): string {
  // Never a used identifier, and never one used before and deleted: a button
  // still pointing at a deleted page must not quietly start opening a new one.
  const taken = new Set(pageIds(doc))
  for (const el of doc.elements) if (el.action?.kind === 'page') taken.add(el.action.pageId)
  for (;;) {
    const id = `page-${Math.random().toString(36).slice(2, 7)}`
    if (!taken.has(id)) return id
  }
}

/** A button on a page, sized for a finger and dropped where it can be seen. */
export function newButton(doc: LayoutDocument, page: string, action: ButtonAction): LayoutElement {
  const z = Math.max(0, ...doc.elements.map((e) => e.z ?? 0)) + 10
  const id = `button-${Math.random().toString(36).slice(2, 8)}`
  const base = { id, kind: 'button' as const, z, screens: ['choosing' as const], pages: [page], action }

  // After the buttons already on the page, the same size and look, in the next
  // place along: a second button dropped in the middle covered the first.
  if (action.kind !== 'back') {
    const slot = nextSlot(doc, page)
    if (slot) return { ...slot.look, ...base, x: slot.x, y: slot.y }
  }
  const w = Math.min(Math.round(doc.canvas.width * 0.42), doc.canvas.width)
  const h = Math.min(Math.max(Math.round(doc.canvas.height * 0.12), 120), doc.canvas.height)
  return {
    ...base,
    x: Math.round((doc.canvas.width - w) / 2),
    y: Math.round((doc.canvas.height - h) / 2),
    w,
    h,
    style:
      action.kind === 'back'
        ? { background: '--bg-surface', colour: '--ink-strong', radius: 20, border: '--border-solid' }
        : { background: '--accent-solid', colour: '#ffffff', radius: 24, shadow: true },
  }
}

/**
 * Where the next button on a page goes: after its last service or page button,
 * in the columns those buttons stand in, drawn like it. Nothing when the page has
 * no such button, or the next place is off the canvas.
 */
export function nextSlot(
  doc: LayoutDocument,
  page: string,
): { x: number; y: number; look: Omit<LayoutElement, 'id' | 'kind' | 'x' | 'y' | 'z' | 'action' | 'content' | 'subtitle' | 'icon' | 'assetId'> } | null {
  const buttons = doc.elements
    .filter((el) => el.kind === 'button' && (el.action?.kind === 'service' || el.action?.kind === 'page') && onMenuPage(el, page))
    .sort((a, b) => a.y - b.y || a.x - b.x)
  const last = buttons[buttons.length - 1]
  if (!last) return null
  const columns = [...new Set(buttons.map((b) => b.x))].sort((a, b) => a - b)
  const rows = [...new Set(buttons.map((b) => b.y))].sort((a, b) => a - b)
  const gap = rows.length > 1 ? Math.max(0, rows[1]! - rows[0]! - last.h) : Math.round(last.h * 0.13)
  const col = columns.indexOf(last.x)
  let x: number
  let y: number
  if (col >= 0 && col < columns.length - 1) {
    // The next column of a row the buttons already make.
    x = columns[col + 1]!
    y = last.y
  } else if (columns.length === 1 && last.x + 2 * last.w + gap <= doc.canvas.width) {
    // One button so far: beside it, if there is room.
    x = last.x + last.w + gap
    y = last.y
  } else {
    x = columns[0]!
    y = last.y + last.h + gap
  }
  if (y + last.h > doc.canvas.height || x + last.w > doc.canvas.width) return null
  const { id: _i, kind: _k, x: _x, y: _y, z: _z, action: _a, content: _c, subtitle: _s, icon: _ic, assetId: _as, ...look } = last
  return { x, y, look }
}

/** A new page after the others, with its back button already on it: a page
 *  nobody can leave is the mistake most worth not making. */
export function addPage(doc: LayoutDocument): { doc: LayoutDocument; pageId: string } {
  const base = withPages(doc)
  const pageId = freshPageId(base)
  const n = base.pages!.length + 1
  const page: LayoutPage = { id: pageId, name: { az: t('designs.ed.pageN', 'az', { n }), en: t('designs.ed.pageN', 'en', { n }), ru: t('designs.ed.pageN', 'ru', { n }) } }
  const back = newButton(base, pageId, { kind: 'back' })
  back.w = Math.round(doc.canvas.width * 0.3)
  back.h = Math.max(Math.round(doc.canvas.height * 0.07), 80)
  back.x = Math.round(doc.canvas.width * 0.05)
  back.y = doc.canvas.height - back.h - Math.round(doc.canvas.height * 0.04)
  return { doc: { ...base, pages: [...base.pages!, page], elements: [...base.elements, back] }, pageId }
}

/** One language of a page's name. */
export function renamePage(doc: LayoutDocument, pageId: string, lang: string, name: string): LayoutDocument {
  const base = withPages(doc)
  return {
    ...base,
    pages: base.pages!.map((p) => (p.id === pageId ? { ...p, name: { ...p.name, [lang]: name } } : p)),
  }
}

/**
 * A page gone, with what was only on it.
 *
 * An element on that page and others keeps the others; one on that page alone
 * goes. A button that opened it is kept with nothing to do — the editor says so,
 * and the server will not publish it — because deleting somebody's button for them
 * hides the consequence of what they just did. The menu cannot be deleted.
 */
export function deletePage(doc: LayoutDocument, pageId: string): LayoutDocument {
  const base = withPages(doc)
  if (base.pages![0]!.id === pageId || !base.pages!.some((p) => p.id === pageId)) return doc
  const elements: LayoutElement[] = []
  for (const el of base.elements) {
    let next = el
    if (el.pages && el.pages.includes(pageId)) {
      const rest = el.pages.filter((p) => p !== pageId)
      if (rest.length === 0) continue
      next = { ...next, pages: rest }
    }
    if (next.action?.kind === 'page' && next.action.pageId === pageId) {
      const { action: _gone, ...rest } = next
      next = rest
    }
    elements.push(next)
  }
  return { ...base, pages: base.pages!.filter((p) => p.id !== pageId), elements }
}

/** Whether an element is drawn on a page of the choosing screen. */
export function onMenuPage(el: LayoutElement, page: string): boolean {
  if (el.screens && el.screens.length > 0 && !el.screens.includes('choosing')) return false
  return !el.pages || el.pages.length === 0 || el.pages.includes(page)
}

/** What the server refuses about pages and buttons, in the editor's words.
 *  The sentences alone, kept for the tests; the editor reads pageFaults. */
export function pageProblems(doc: LayoutDocument, lang: StaffLanguage = 'en'): string[] {
  return pageFaults(doc, lang).map((f) => f.text)
}

/** pageProblems, each with the elements it is about. */
export function pageFaults(doc: LayoutDocument, lang: StaffLanguage = 'en'): Fault[] {
  if (doc.kind !== 'kiosk') return []
  const found: Fault[] = []
  const ids = pageIds(doc)
  const menu = ids[0]!
  const known = new Set(ids)

  for (const el of doc.elements) {
    const say = (text: string) => found.push({ text, ids: [el.id] })
    for (const p of el.pages ?? []) {
      if (!known.has(p)) say(t('designs.fault.pageGone', lang))
    }
    if (el.kind !== 'button') continue
    const a = el.action
    if (!a) {
      say(t('designs.fault.buttonIdle', lang))
      continue
    }
    if (a.kind === 'service' && !a.serviceId) say(t('designs.fault.buttonNoService', lang))
    if (a.kind === 'page') {
      if (!known.has(a.pageId)) say(t('designs.fault.buttonNoPage', lang))
      else if (el.pages?.includes(a.pageId)) say(t('designs.fault.buttonOwnPage', lang, { page: pageLabel(doc, a.pageId, lang) }))
    }
    if (a.kind === 'back' && onMenuPage(el, menu)) say(t('designs.fault.backOnMenu', lang))
  }

  const ways = doc.elements.filter((el) => el.kind === 'services' || (el.kind === 'button' && el.action?.kind === 'service'))
  if (ways.length === 0) found.push({ text: t('designs.fault.noTicketWay', lang), ids: [] })
  else if (ways.every((el) => el.hidden)) found.push({ text: t('designs.fault.ticketWayHidden', lang), ids: ways.map((el) => el.id) })

  for (const page of ids) {
    const grids = doc.elements.filter((el) => el.kind === 'services' && onMenuPage(el, page))
    if (grids.length > 1) found.push({ text: t('designs.fault.pageTwoGrids', lang, { page: pageLabel(doc, page, lang) }), ids: grids.map((el) => el.id) })
  }
  return found
}

/** The pages after the menu that no button opens. */
export function unopenedPages(doc: LayoutDocument): string[] {
  if (doc.kind !== 'kiosk' || !doc.pages || doc.pages.length < 2) return []
  return pageIds(doc)
    .slice(1)
    .filter(
      (page) =>
        !doc.elements.some(
          (el) => el.kind === 'button' && el.action?.kind === 'page' && el.action.pageId === page && !(el.pages ?? []).includes(page),
        ),
    )
}

/** What is worth saying about pages without refusing to publish. */
export function pageWarnings(doc: LayoutDocument, lang: StaffLanguage = 'en'): string[] {
  if (doc.kind !== 'kiosk') return []
  const found: string[] = []
  const switches = doc.elements.filter((el) => el.kind === 'languages' && !el.hidden)
  if (doc.canvas.skipWelcome && switches.length > 0 && switches.every((el) => el.screens?.length === 1 && el.screens[0] === 'attract')) {
    found.push(t('designs.warn.switchOnWelcome', lang))
  }
  if (!doc.pages || doc.pages.length < 2) return found
  const [menu, ...rest] = pageIds(doc)
  const unopened = unopenedPages(doc)
  for (const page of rest) {
    if (unopened.includes(page)) found.push(t('designs.warn.pageUnopened', lang, { page: pageLabel(doc, page, lang) }))
    const back = doc.elements.some((el) => el.kind === 'button' && el.action?.kind === 'back' && onMenuPage(el, page))
    if (!back) found.push(t('designs.warn.pageNoBack', lang, { page: pageLabel(doc, page, lang) }))
  }
  void menu
  return found
}

/**
 * A new element on a kiosk, put on the screen being drawn on — and on the page,
 * when the menu has more than one.
 *
 * Three kinds belong to one screen whatever tab is open: the number and the wait
 * are the ticket screen's. Everything else, the language switch included, goes
 * where the designer is looking. It used to go on every screen, so a ticket
 * number added on the Ticket tab also sat on the menu, and the design could not be
 * published.
 */
export function placeOnScreen(
  el: LayoutElement,
  doc: LayoutDocument,
  screen: Screen | undefined,
  page?: string,
): LayoutElement {
  if (doc.kind === 'counter' && isCounter(screen)) return { ...el, screens: [COUNTER_HOME[el.kind] ?? screen] }
  if (doc.kind === 'display') return screen === 'call' ? { ...el, screens: ['call'] } : el
  if (doc.kind !== 'kiosk' || !isKiosk(screen)) return el
  const own: Partial<Record<LayoutElement['kind'], KioskScreen>> = {
    ticketNumber: 'issued',
    ticketFacts: 'issued',
    services: 'choosing',
    button: 'choosing',
  }
  const on = own[el.kind] ?? screen
  const placed: LayoutElement = { ...el, screens: [on] }
  // A language switch on the menu stands on every page of it.
  if (el.kind === 'languages') return placed
  if (on === 'choosing' && page && pageIds(doc).length > 1) placed.pages = [page]
  else if (on === 'choosing' && page && (el.kind === 'services' || el.kind === 'button')) placed.pages = [page]
  return placed
}

/**
 * Whether the palette offers a kind where the designer is drawing.
 *
 * Not a second of what a design holds one of — the server refuses to save such a
 * draft, so every edit after it would fail (found 1 October 2026) — and, on a
 * kiosk, not what belongs on another screen: the ticket number and the wait are
 * the ticket screen's, a grid the menu's.
 * Offered on the wrong tab, they were added where the designer could not see them.
 */
export function offeredHere(doc: LayoutDocument, kind: LayoutElement['kind'], screen: Screen | undefined, page?: string): boolean {
  const has = (k: LayoutElement['kind']) => doc.elements.some((el) => el.kind === k)
  if (kind === 'ticketNumber' && has(kind)) return false
  if (doc.kind === 'counter' && isCounter(screen)) return (COUNTER_HOME[kind] ?? screen) === screen
  if (doc.kind === 'display') return screen === 'call' ? CALLED_KINDS.includes(kind) : !CALL_ONLY.includes(kind)
  if (doc.kind !== 'kiosk' || !isKiosk(screen)) return true
  if (kind === 'media' && screen === 'issued') return false
  const home: Partial<Record<LayoutElement['kind'], KioskScreen>> = {
    ticketNumber: 'issued',
    ticketFacts: 'issued',
    services: 'choosing',
  }
  if (home[kind] && home[kind] !== screen) return false
  if (kind === 'services' && page && doc.elements.some((el) => el.kind === 'services' && onMenuPage(el, page))) return false
  return true
}

/** Where a kiosk or counter element is drawn: the screen and page being looked at when
 *  it is there, else the first it appears on. A fault picked in the list opens it. */
export function whereIs(
  el: LayoutElement,
  doc: LayoutDocument,
  screen: Screen,
  page: string,
): { screen: Screen; page: string } {
  if (doc.kind === 'counter') {
    if (onScreen(el, screen)) return { screen, page }
    return { screen: el.screens?.find(isCounter) ?? screen, page }
  }
  if (doc.kind === 'display') return { screen: onCall(el) ? 'call' : 'board', page }
  if (doc.kind !== 'kiosk' || onScreen(el, screen, screen === 'choosing' ? page : undefined)) return { screen, page }
  const ids = pageIds(doc)
  const target = (el.screens?.[0] ?? 'choosing') as KioskScreen
  if (target !== 'choosing') return { screen: target, page }
  return { screen: target, page: el.pages?.find((p) => ids.includes(p)) ?? ids[0]! }
}

const isCounter = (s: Screen | undefined): s is (typeof COUNTER_SCREENS)[number] => !!s && (COUNTER_SCREENS as readonly Screen[]).includes(s)
const isKiosk = (s: Screen | undefined): s is KioskScreen => s === 'attract' || s === 'choosing' || s === 'issued'
